import { Flex, FormControl, FormHelperText, FormLabel, Input, SimpleGrid } from '@chakra-ui/react';

/** The bounds and defaults the service holds, so a form cannot offer what it would refuse. */
export const ADVANCED_LIMITS = {
  maxDisplayPeriodDays: 365,
  maxReminderDays: 90,
  defaultDisplayPeriodDays: 7,
  defaultNominationReminderLeadDays: 3,
  defaultDecisionReminderLeadDays: 2,
  defaultDecisionReminderIntervalDays: 2,
};

export interface AdvancedFormState {
  nominationsPerNominee: string;
  displayPeriodDays: string;
  nominationReminderLeadDays: string;
  decisionReminderLeadDays: string;
  decisionReminderIntervalDays: string;
}

export const emptyAdvanced: AdvancedFormState = {
  nominationsPerNominee: '',
  displayPeriodDays: '',
  nominationReminderLeadDays: '',
  decisionReminderLeadDays: '',
  decisionReminderIntervalDays: '',
};

/** A blank field is left to the service's own default rather than sent as a guess at it. */
const toNumber = (value: string) => (value === '' ? undefined : Number(value));

export function toAdvancedRequest(form: AdvancedFormState) {
  return {
    nominationsPerNominee: toNumber(form.nominationsPerNominee),
    displayPeriodDays: toNumber(form.displayPeriodDays),
    nominationReminderLeadDays: toNumber(form.nominationReminderLeadDays),
    decisionReminderLeadDays: toNumber(form.decisionReminderLeadDays),
    decisionReminderIntervalDays: toNumber(form.decisionReminderIntervalDays),
  };
}

export function fromAdvanced(campaign: {
  nominationsPerNominee?: number | null;
  displayPeriodDays?: number | null;
  nominationReminderLeadDays?: number | null;
  decisionReminderLeadDays?: number | null;
  decisionReminderIntervalDays?: number | null;
}): AdvancedFormState {
  const asText = (value: number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

  return {
    nominationsPerNominee: asText(campaign.nominationsPerNominee),
    displayPeriodDays: asText(campaign.displayPeriodDays),
    nominationReminderLeadDays: asText(campaign.nominationReminderLeadDays),
    decisionReminderLeadDays: asText(campaign.decisionReminderLeadDays),
    decisionReminderIntervalDays: asText(campaign.decisionReminderIntervalDays),
  };
}

const whole = (value: string, min: number, max: number) => {
  if (value === '') return true;
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= min && parsed <= max;
};

/** What is wrong with the advanced settings, as a sentence, or nothing where they are sound. */
export function advancedError(form: AdvancedFormState): string | undefined {
  const { maxReminderDays, maxDisplayPeriodDays } = ADVANCED_LIMITS;

  if (!whole(form.nominationsPerNominee, 1, 1000)) {
    return 'Nominations for the same colleague must be a whole number of at least one.';
  }
  if (!whole(form.displayPeriodDays, 1, maxDisplayPeriodDays)) {
    return `An announcement can be shown for between 1 and ${maxDisplayPeriodDays} days.`;
  }
  if (!whole(form.nominationReminderLeadDays, 1, maxReminderDays)) {
    return `The nominations reminder must be between 1 and ${maxReminderDays} days before they close.`;
  }
  if (!whole(form.decisionReminderLeadDays, 0, maxReminderDays)) {
    return `The decision reminder must be between 0 and ${maxReminderDays} days.`;
  }
  if (!whole(form.decisionReminderIntervalDays, 1, maxReminderDays)) {
    return `Decision reminders must repeat between 1 and ${maxReminderDays} days apart.`;
  }

  return undefined;
}

interface AdvancedFieldsProps {
  form: AdvancedFormState;
  setField: <K extends keyof AdvancedFormState>(key: K, value: AdvancedFormState[K]) => void;
}

/** Settings a campaign runs perfectly well without. Left blank, each takes the service's own default. */
function AdvancedFields({ form, setField }: AdvancedFieldsProps) {
  const { defaultDisplayPeriodDays } = ADVANCED_LIMITS;

  return (
    <Flex flexDir="column" gap="16px" data-testid="advanced-fields">
      <FormControl>
        <FormLabel>Nominations for the same colleague</FormLabel>
        <Input
          type="number"
          min={1}
          value={form.nominationsPerNominee}
          onChange={(event) => setField('nominationsPerNominee', event.target.value)}
          placeholder="1"
          data-testid="nominations-per-nominee-input"
        />
        <FormHelperText>
          How many times one person may put the same colleague forward. Left blank, a colleague is
          nominated once and the nomination is edited rather than repeated.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Announcement shown for</FormLabel>
        <Input
          type="number"
          min={1}
          max={ADVANCED_LIMITS.maxDisplayPeriodDays}
          value={form.displayPeriodDays}
          onChange={(event) => setField('displayPeriodDays', event.target.value)}
          placeholder={String(defaultDisplayPeriodDays)}
          data-testid="display-period-input"
        />
        <FormHelperText>
          Days the winner stays on employees&apos; announcements. Left blank,{' '}
          {defaultDisplayPeriodDays} days.
        </FormHelperText>
      </FormControl>

      <SimpleGrid columns={{ base: 1, lg: 3 }} gap="16px">
        <FormControl>
          <FormLabel>Nominations reminder</FormLabel>
          <Input
            type="number"
            min={1}
            max={ADVANCED_LIMITS.maxReminderDays}
            value={form.nominationReminderLeadDays}
            onChange={(event) => setField('nominationReminderLeadDays', event.target.value)}
            placeholder={String(ADVANCED_LIMITS.defaultNominationReminderLeadDays)}
            data-testid="nomination-reminder-input"
          />
          <FormHelperText>
            Days before nominations close that everyone who has not nominated is reminded. Once per
            run.
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Decision reminder</FormLabel>
          <Input
            type="number"
            min={0}
            max={ADVANCED_LIMITS.maxReminderDays}
            value={form.decisionReminderLeadDays}
            onChange={(event) => setField('decisionReminderLeadDays', event.target.value)}
            placeholder={String(ADVANCED_LIMITS.defaultDecisionReminderLeadDays)}
            data-testid="decision-reminder-input"
          />
          <FormHelperText>
            Days before the review closes that the campaign managers are first chased.
          </FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Then every</FormLabel>
          <Input
            type="number"
            min={1}
            max={ADVANCED_LIMITS.maxReminderDays}
            value={form.decisionReminderIntervalDays}
            onChange={(event) => setField('decisionReminderIntervalDays', event.target.value)}
            placeholder={String(ADVANCED_LIMITS.defaultDecisionReminderIntervalDays)}
            data-testid="decision-reminder-interval-input"
          />
          <FormHelperText>Days between chasers while the campaign stays undecided.</FormHelperText>
        </FormControl>
      </SimpleGrid>
    </Flex>
  );
}

export default AdvancedFields;
